import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SITE_ACCOUNT } from '../shared/whatsapp-routing';

@Injectable({ providedIn: 'root' })
export class WebAnalyticsService {
  private readonly endpoint = 'https://5t4msj7ycbtuaqv3c7arjp2nam0qizez.lambda-url.us-east-1.on.aws/';
  private readonly visitorId = this.identity('mvs_web_visitor', localStorage);
  private readonly sessionId = this.identity('mvs_web_session', sessionStorage);
  private readonly attribution = this.loadAttribution();
  private started = false;

  constructor(private router: Router) {}

  start(): void {
    if (this.started) { return; }
    this.started = true;
    this.router.events.pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => this.trackRoute(event.urlAfterRedirects || event.url));
    this.trackRoute(this.router.url || window.location.pathname);
  }

  track(eventType: string, details: any = {}): void {
    const payload = Object.assign({
      eventType,
      site: SITE_ACCOUNT,
      visitorId: this.visitorId,
      sessionId: this.sessionId,
      path: window.location.pathname + window.location.search,
      url: window.location.href,
      referrer: document.referrer || '',
      sourceType: this.attribution.sourceType,
      utmSource: this.attribution.utmSource,
      utmMedium: this.attribution.utmMedium,
      utmCampaign: this.attribution.utmCampaign,
      utmContent: this.attribution.utmContent
    }, details || {});
    fetch(this.endpoint, {
      method: 'POST',
      mode: 'cors',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).catch(() => {});
  }

  isFacebookOrCampaignVisit(): boolean {
    const params = new URLSearchParams(window.location.search);
    const hasCampaignParameter = Boolean(
      params.get('utm_source') ||
      params.get('utm_medium') ||
      params.get('utm_campaign') ||
      params.get('utm_content') ||
      params.get('ad_id') ||
      params.get('fbclid')
    );
    const referrer = (document.referrer || '').toLowerCase();
    const hasFacebookReferrer = /facebook\.com|instagram\.com|messenger\.com/.test(referrer);
    return this.attribution.sourceType === 'campaign' || hasCampaignParameter || hasFacebookReferrer;
  }

  private trackRoute(url: string): void {
    this.track('page_view');
    const match = `${url || ''}`.match(/\/a\/([^?;/]+)|\/p(?:;id=|\/)([^?;/]+)/i);
    const sku = match ? (match[1] || match[2]) : '';
    if (sku) {
      this.track('product_view', { productSku: decodeURIComponent(sku) });
    }
  }

  private loadAttribution(): any {
    const key = 'mvs_web_attribution';
    try {
      const saved = sessionStorage.getItem(key);
      if (saved) { return JSON.parse(saved); }
      const params = new URLSearchParams(window.location.search);
      const utmSource = params.get('utm_source') || '';
      const utmMedium = params.get('utm_medium') || '';
      const utmCampaign = params.get('utm_campaign') || '';
      const utmContent = params.get('utm_content') || params.get('ad_id') || '';
      const campaign = Boolean(utmSource || utmMedium || utmCampaign || utmContent || params.get('fbclid') || params.get('gclid'));
      const attribution = { sourceType: campaign ? 'campaign' : 'organic', utmSource, utmMedium, utmCampaign, utmContent };
      sessionStorage.setItem(key, JSON.stringify(attribution));
      return attribution;
    } catch (_) {
      return { sourceType: 'organic', utmSource: '', utmMedium: '', utmCampaign: '', utmContent: '' };
    }
  }

  private identity(key: string, storage: Storage): string {
    try {
      const current = storage.getItem(key);
      if (current) { return current; }
      const value = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
      storage.setItem(key, value);
      return value;
    } catch (_) {
      return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    }
  }
}
